import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Radio, RadioGroup, FormControlLabel, FormLabel, Chip, Slider,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, Switch, Alert, CircularProgress
} from "@mui/material";
import { ExpandMore, ArrowBack } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCampaignDetails, useCampaignActions, useMarketingSettings, usePresetPreview, defaultFormData, validateForm,
  holdoutApplies, suggestedSlots, toBdInput, fromBdInput, MARKETING_DEFAULTS, DAY_NAMES
} from "./hooks/useCampaign";
import { countSmsParts, previewSms, formatBdt } from "./utils/smsParts";
import UserSelector from "./UserSelector";
import "./Campaign.css";

const LANDINGS = [
  ["dashboard", "Dashboard"],
  ["packages", "Packages (member's own profile with the package list open)"],
  ["checkoutProfile", "The profile they tried to unlock (falls back to packages)"],
  ["custom", "Custom URLs"]
];

const PRESET_OPTION_LABELS = {
  fromHours: "From (hours ago)",
  toHours: "To (hours ago)",
  minAmount: "Minimum amount (৳)",
  days: "Within the last (days)",
  withinDays: "Within (days)",
  fromDays: "From (days ago)",
  toDays: "To (days ago)",
  activeDays: "Active on at least (days)",
  maxAmount: "Maximum amount (৳, 0 = any)",
  inactiveDays: "Not online for (days)",
  max: "At most (members, 0 = all)",
  onlineWithinDays: "Online within (days, 0 = all)"
};
// Presets that leave out members who paid since (the others are about buyers or everyone)
const DROPS_PAYERS = ["abandonedCheckout", "offerInterest", "verifiedNeverPaid", "engagedNeverPaid", "packageEnded"];

const fmtSlot = (d) => d.toLocaleString("en-GB", { timeZone: "Asia/Dhaka", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const CreateCampaign = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const isEdit = !!campaignId;
  const { fetch } = useCampaignDetails();
  const { save } = useCampaignActions();
  const { settings, presets, fetch: fetchSettings } = useMarketingSettings();
  const { preview, loading: previewLoading, fetch: fetchPreview } = usePresetPreview();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(defaultFormData);

  useEffect(() => {
    (async () => {
      const s = await fetchSettings();
      const defaults = {
        holdoutPercent: s?.features?.holdout === false ? 0 : s?.holdoutPercent ?? MARKETING_DEFAULTS.holdoutPercent,
        attributionDays: s?.attributionDays ?? MARKETING_DEFAULTS.attributionDays
      };
      if (isEdit) {
        const r = await fetch(campaignId);
        if (r.success) {
          setForm({
            ...defaultFormData,
            ...r.data,
            targetAudience: { ...defaultFormData.targetAudience, ...r.data.targetAudience },
            smsMessage: { ...defaultFormData.smsMessage, landing: "custom", ...r.data.smsMessage },
            schedule: { ...defaultFormData.schedule, ...r.data.schedule },
            abTest: { ...defaultFormData.abTest, ...r.data.abTest },
            holdoutPercent: r.data.holdoutPercent ?? defaults.holdoutPercent,
            attributionDays: r.data.attributionDays ?? defaults.attributionDays
          });
        }
      } else {
        setForm(f => ({ ...f, ...defaults }));
      }
    })();
  }, [campaignId]);

  // Live audience size for presets
  const preset = form.targetAudience.preset;
  useEffect(() => {
    if (form.targetAudience.filterType !== "preset") return;
    const t = setTimeout(() => fetchPreview(preset?.key, preset?.options), 400);
    return () => clearTimeout(t);
  }, [form.targetAudience.filterType, preset?.key, JSON.stringify(preset?.options)]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setNested = (parent, field, value) => setForm(f => ({ ...f, [parent]: { ...f[parent], [field]: value } }));
  const setFilter = (field, value) => setForm(f => ({
    ...f, targetAudience: { ...f.targetAudience, customFilters: { ...f.targetAudience.customFilters, [field]: value } }
  }));
  const setPreset = (key, options) => setForm(f => ({ ...f, targetAudience: { ...f.targetAudience, preset: { key, options } } }));

  const handleUserSelect = (userId, isSelected) => {
    const ids = form.targetAudience.selectedUserIds || [];
    const newIds = isSelected ? [...ids.filter(id => id !== userId), userId] : ids.filter(id => id !== userId);
    setForm(f => ({ ...f, targetAudience: { ...f.targetAudience, selectedUserIds: newIds, estimatedReach: newIds.length } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm(form);
    if (error) return toast.error(error);
    setLoading(true);
    const payload = {
      ...form,
      ...(isEdit && { campaignId }),
      holdoutPercent: holdoutApplies(form) ? Number(form.holdoutPercent) : 0,
      attributionDays: Number(form.attributionDays),
      abTest: form.type === "sms" ? form.abTest : { ...form.abTest, enabled: false },
      perUserBestHour: form.type !== "push" && !form.abTest?.enabled ? !!form.perUserBestHour : false
    };
    if (await save(payload, isEdit)) navigate("/campaigns");
    setLoading(false);
  };

  const filters = form.targetAudience.customFilters;
  const hasSms = form.type === "sms" || form.type === "both";
  const presetInfo = presets.find(p => p.key === preset?.key);
  const presetOptions = { ...(presetInfo?.options || {}), ...(preset?.options || {}) };

  // SMS cost preview: {TrackingLink} counted at its real length
  const smsRate = settings?.costs?.smsRatePerPart ?? MARKETING_DEFAULTS.costs.smsRatePerPart;
  const finalSms = previewSms(form.smsMessage.content, settings?.shortLinkStyle);
  const smsInfo = countSmsParts(finalSms);
  const reach = (form.targetAudience.filterType === "selected"
    ? form.targetAudience.selectedUserIds?.length
    : form.targetAudience.filterType === "preset"
      ? (hasSms ? preview?.withMobile : preview?.withPush) ?? preview?.count
      : form.targetAudience.estimatedReach) || 0;
  const sendShare = holdoutApplies(form) ? 1 - (Number(form.holdoutPercent) || 0) / 100 : 1;
  const smsRecipients = Math.round(reach * sendShare);
  const costPerPerson = smsInfo.parts * smsRate;
  const slots = suggestedSlots(settings);
  const abAllowed = settings?.features?.sendTimeAB !== false && form.type === "sms";

  return (
    <Box className="campaign-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <Box className="campaign-header">
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/campaigns")}>Back</Button>
        <Typography variant="h5">{isEdit ? "Edit Campaign" : "Create Campaign"}</Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        {/* Basic Info */}
        <Paper className="form-section">
          <Typography variant="h6" gutterBottom>Basic Information</Typography>
          <TextField label="Campaign Name" fullWidth required value={form.name} onChange={e => set("name", e.target.value)} margin="normal" />
          <TextField label="Description" fullWidth multiline rows={2} value={form.description} onChange={e => set("description", e.target.value)} margin="normal" />
          <FormControl fullWidth margin="normal">
            <InputLabel>Campaign Type</InputLabel>
            <Select value={form.type} label="Campaign Type" onChange={e => set("type", e.target.value)}>
              <MenuItem value="push">Push Only</MenuItem>
              <MenuItem value="sms">SMS Only</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        {/* Target Audience */}
        <Paper className="form-section">
          <Typography variant="h6" gutterBottom>Target Audience</Typography>
          <FormControl component="fieldset" margin="normal">
            <FormLabel>Select Audience</FormLabel>
            <RadioGroup value={form.targetAudience.filterType} onChange={e => setNested("targetAudience", "filterType", e.target.value)}>
              <FormControlLabel value="all" control={<Radio />} label="All Users" />
              <FormControlLabel value="preset" control={<Radio />} label="Ready-made audience (abandoned checkout, package expiring …)" />
              <FormControlLabel value="custom" control={<Radio />} label="Custom Filters" />
              <FormControlLabel value="selected" control={<Radio />} label="Select Users" />
            </RadioGroup>
          </FormControl>

          {form.targetAudience.filterType === "preset" && (
            <Box sx={{ mt: 1 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Audience</InputLabel>
                <Select value={preset?.key || ""} label="Audience" onChange={e => setPreset(e.target.value, {})}>
                  {presets.map(p => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                </Select>
              </FormControl>
              {presetInfo && <Typography variant="body2" color="text.secondary">{presetInfo.description}.{DROPS_PAYERS.includes(presetInfo.key) && " People who paid since are left out automatically."}</Typography>}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                {Object.entries(presetInfo?.options || {}).map(([k]) => (
                  <TextField key={k} type="number" size="small" label={PRESET_OPTION_LABELS[k] || k} sx={{ width: 190 }}
                    value={presetOptions[k] ?? ""} onChange={e => setPreset(preset.key, { ...preset.options, [k]: e.target.value === "" ? undefined : Number(e.target.value) })} />
                ))}
              </Box>
              <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                {previewLoading ? <CircularProgress size={16} /> : preview && (
                  <Typography variant="body2">
                    Right now: <b>{preview.count.toLocaleString()}</b> members · {preview.withMobile.toLocaleString()} with a number · {preview.withPush.toLocaleString()} with push
                    {preview.amounts && ` · checkouts: ${Object.entries(preview.amounts).map(([a, n]) => `৳${a}×${n}`).join(", ")}`}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">The audience is worked out again at send time.</Typography>
            </Box>
          )}

          {form.targetAudience.filterType === "custom" && (
            <Box sx={{ mt: 2 }}>
              <Accordion><AccordionSummary expandIcon={<ExpandMore />}><Typography>Demographics</Typography></AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth margin="normal"><InputLabel>Gender</InputLabel>
                    <Select value={filters.gender} label="Gender" onChange={e => setFilter("gender", e.target.value)}>
                      <MenuItem value="all">All</MenuItem><MenuItem value="male">Male</MenuItem><MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography gutterBottom>Age: {filters.ageRange.min} - {filters.ageRange.max}</Typography>
                  <Slider value={[filters.ageRange.min, filters.ageRange.max]} min={18} max={70} onChange={(e, v) => setFilter("ageRange", { min: v[0], max: v[1] })} valueLabelDisplay="auto" />
                </AccordionDetails>
              </Accordion>

              <Accordion><AccordionSummary expandIcon={<ExpandMore />}><Typography>Subscription</Typography></AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth><InputLabel>Status</InputLabel>
                    <Select multiple value={filters.subscriptionStatus} onChange={e => setFilter("subscriptionStatus", e.target.value)}
                      renderValue={sel => <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{sel.map(v => <Chip key={v} label={v} size="small" />)}</Box>}>
                      {["free", "premium", "expired"].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </AccordionDetails>
              </Accordion>

              <Accordion><AccordionSummary expandIcon={<ExpandMore />}><Typography>Engagement</Typography></AccordionSummary>
                <AccordionDetails>
                  <TextField label="Last Active Within (days)" type="number" fullWidth value={filters.lastActiveWithin || ""} onChange={e => setFilter("lastActiveWithin", e.target.value ? parseInt(e.target.value) : null)} margin="normal" />
                  {[["profileCompleted", "Profile Completed"], ["hasPhoto", "Has Photo"], ["hasCV", "Has CV"]].map(([k, l]) => (
                    <FormControlLabel key={k} control={<Checkbox checked={filters[k]} onChange={e => setFilter(k, e.target.checked)} />} label={l} />
                  ))}
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          {form.targetAudience.filterType === "selected" && (
            <Box sx={{ mt: 2 }}>
              <UserSelector selectedUserIds={form.targetAudience.selectedUserIds || []} onUserSelection={handleUserSelect} onRemoveUser={id => handleUserSelect(id, false)} />
            </Box>
          )}

          {form.targetAudience.filterType !== "preset" && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Estimated Reach: {(form.targetAudience.filterType === "selected" ? form.targetAudience.selectedUserIds?.length : form.targetAudience.estimatedReach) || 0} users
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Message Content */}
        <Paper className="form-section">
          <Typography variant="h6" gutterBottom>Message Content</Typography>

          {(form.type === "push" || form.type === "both") && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Push Notification</Typography>
              <TextField label="Title" fullWidth required value={form.pushMessage.title} onChange={e => setNested("pushMessage", "title", e.target.value)} margin="normal" />
              <TextField label="Body" fullWidth required multiline rows={3} value={form.pushMessage.body} onChange={e => setNested("pushMessage", "body", e.target.value)} margin="normal" />
              <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Navigation URLs</Typography>
                <TextField label="Web App URL" fullWidth size="small" value={form.pushMessage.redirectUrlWeb} onChange={e => setNested("pushMessage", "redirectUrlWeb", e.target.value)} margin="normal" />
                <TextField label="Mobile App Route" fullWidth size="small" value={form.pushMessage.redirectUrlMobile} onChange={e => setNested("pushMessage", "redirectUrlMobile", e.target.value)} margin="normal" />
              </Box>
            </Box>
          )}

          {hasSms && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>SMS Message</Typography>
              <TextField label="Content" fullWidth required multiline rows={3} value={form.smsMessage.content} onChange={e => setNested("smsMessage", "content", e.target.value)} margin="normal"
                helperText={`Use {TrackingLink} for trackable links. ${form.smsMessage.content.length}/160`} inputProps={{ maxLength: 160 }} />
              {form.smsMessage.content.trim() && (
                <Box sx={{ mt: 1, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    What each person receives{form.smsMessage.content.includes("{TrackingLink}") ? " (example link)" : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", mb: 1.5 }}>{finalSms}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Chip size="small" label={smsInfo.encoding === "Unicode" ? "Bangla / Unicode" : "English (GSM)"} />
                    <Chip size="small" label={`${smsInfo.units} characters`} />
                    <Chip size="small" color={smsInfo.parts > 1 ? "warning" : "success"} label={`${smsInfo.parts} SMS part${smsInfo.parts === 1 ? "" : "s"}`} />
                    <Chip size="small" label={`${formatBdt(costPerPerson)} per person`} />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1.5 }}>
                    {reach > 0
                      ? <>Estimated cost: <b>{formatBdt(smsRecipients * costPerPerson)}</b> for about {smsRecipients.toLocaleString()} SMS{sendShare < 1 ? " (holdout not sent)" : ""}</>
                      : "Save the campaign to see the audience size and the total cost."}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {smsInfo.encoding === "Unicode"
                      ? "Bangla: 70 characters fit in 1 SMS; longer messages are billed per 67 characters."
                      : "English: 160 characters fit in 1 SMS; longer messages are billed per 153 characters."}
                    {" "}Price {formatBdt(smsRate)} per part (Marketing settings). Members who turned promotional SMS off,
                    are on the do-not-call list or already got {settings?.timing?.promoSmsCap?.count ?? 2} SMS in {settings?.timing?.promoSmsCap?.days ?? 7} days are skipped.
                  </Typography>
                </Box>
              )}
              {form.smsMessage.content.includes("{TrackingLink}") && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Where the link opens</Typography>
                  <FormControl fullWidth size="small" margin="dense">
                    <InputLabel>Landing screen</InputLabel>
                    <Select value={form.smsMessage.landing || "custom"} label="Landing screen" onChange={e => setNested("smsMessage", "landing", e.target.value)}>
                      {LANDINGS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {(form.smsMessage.landing || "custom") === "custom" && (
                    <>
                      <TextField label="Web App URL" fullWidth size="small" value={form.smsMessage.redirectUrlWeb} onChange={e => setNested("smsMessage", "redirectUrlWeb", e.target.value)} margin="normal" />
                      <TextField label="Mobile Deep Link" fullWidth size="small" value={form.smsMessage.redirectUrlMobile} onChange={e => setNested("smsMessage", "redirectUrlMobile", e.target.value)} margin="normal"
                        helperText="Use the query form Expo Router understands, e.g. bibahobangla://viewprofile?id=…" />
                    </>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Members without the app go to {settings?.noApp?.android === "web" ? "the web app" : "the Play Store"} on Android (Marketing settings).
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        {/* When to send */}
        <Paper className="form-section">
          <Typography variant="h6" gutterBottom>When to Send</Typography>
          <RadioGroup row value={form.schedule?.type || "immediate"} onChange={e => setNested("schedule", "type", e.target.value)}>
            <FormControlLabel value="immediate" control={<Radio />} label="When I press Execute" />
            <FormControlLabel value="scheduled" control={<Radio />} label="At a set time" />
          </RadioGroup>
          {form.schedule?.type === "scheduled" && (
            <Box>
              <TextField type="datetime-local" label="Send at (Bangladesh time)" InputLabelProps={{ shrink: true }} margin="normal"
                value={toBdInput(form.schedule.sendAt)} onChange={e => setNested("schedule", "sendAt", fromBdInput(e.target.value))} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: "100%" }}>Best times from our data:</Typography>
                {slots.map(d => (
                  <Chip key={d.toISOString()} label={fmtSlot(d)} onClick={() => setNested("schedule", "sendAt", d)} color="primary" variant="outlined" />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Press Execute to arm it; it goes out at this time. Preferred days: {(settings?.timing?.preferredDays || [3, 0]).map(d => DAY_NAMES[d]).join(", ")}.
              </Typography>
            </Box>
          )}
          {hasSms && (
            <Alert severity="info" sx={{ mt: 2 }}>
              SMS never go out between {settings?.timing?.quietHours?.start || "23:00"} and {settings?.timing?.quietHours?.end || "08:00"}; they wait until the morning.
            </Alert>
          )}

          {abAllowed && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel control={<Switch checked={!!form.abTest?.enabled} onChange={e => setNested("abTest", "enabled", e.target.checked)} />}
                label="Send-time test: half at one time, half at another, and compare the results" />
              {form.abTest?.enabled && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField type="time" size="small" label="Group A" InputLabelProps={{ shrink: true }} value={form.abTest.slotA} onChange={e => setNested("abTest", "slotA", e.target.value)} />
                  <TextField type="time" size="small" label="Group B" InputLabelProps={{ shrink: true }} value={form.abTest.slotB} onChange={e => setNested("abTest", "slotB", e.target.value)} />
                </Box>
              )}
            </Box>
          )}
          {hasSms && !form.abTest?.enabled && (
            <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={!!form.perUserBestHour} onChange={e => set("perUserBestHour", e.target.checked)} />}
              label="Send each member's SMS at the hour they're usually in the app (others in the next send window)" />
          )}
        </Paper>

        {/* Measurement */}
        <Paper className="form-section">
          <Typography variant="h6" gutterBottom>Measure Results</Typography>
          <Typography variant="body2" color="text.secondary">
            After sending, the campaign page shows who paid within the window, the revenue, the cost and the profit or loss.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField label="Count payments within (days)" type="number" margin="normal" sx={{ flex: "1 1 220px" }}
              value={form.attributionDays} onChange={e => set("attributionDays", e.target.value)}
              inputProps={{ min: 1, max: 30, step: 1 }}
              helperText="A payment counts if it happens within this many days after the message (1–30)." />
            {holdoutApplies(form) && settings?.features?.holdout !== false && (
              <TextField label="Holdout (%)" type="number" margin="normal" sx={{ flex: "1 1 220px" }}
                value={form.holdoutPercent} onChange={e => set("holdoutPercent", e.target.value)}
                inputProps={{ min: 0, max: 50, step: 1 }}
                helperText="This share of the audience won't get the message. Comparing them with the rest shows the money it really made (0–50, 0 = send to all)." />
            )}
          </Box>
          {!holdoutApplies(form) && (
            <Typography variant="caption" color="text.secondary">
              No holdout here: {form.type === "both" ? "the holdout would still get the other channel" : "everyone you picked gets it"}. Use an SMS-only or push-only campaign to a filtered audience to measure real profit.
            </Typography>
          )}
        </Paper>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate("/campaigns")}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>{loading ? "Saving..." : isEdit ? "Update" : "Create"}</Button>
        </Box>
      </form>
    </Box>
  );
};

export default CreateCampaign;
